
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Users, Clock, Pin, Lock, Plus, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useSEO } from '@/hooks/useSEO';

interface TopicWithProfile {
  id: string;
  title: string;
  is_pinned: boolean;
  is_locked: boolean;
  posts_count: number;
  views_count: number;
  created_at: string;
  last_posted_at: string;
  user_profiles: {
    display_name: string | null;
  } | null;
}

export default function ForumCategory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: category } = useQuery({
    queryKey: ["forum-category", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_categories")
        .select("*")
        .eq("slug", slug)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // SEO Configuration with dynamic content
  useSEO({
    title: category 
      ? `${category.name} - ChessHatch Forums`
      : "Forum Category - ChessHatch Community",
    description: category
      ? `Discussion topics about ${category.name.toLowerCase()}. Join the ChessHatch community to discuss ${category.description?.toLowerCase() || 'chess strategies and tips'}.`
      : "Join chess discussions and connect with players worldwide in ChessHatch community forums.",
    keywords: `chess forum, ${category?.name?.toLowerCase() || 'chess discussion'}, chess community, chess strategy, chess tips, chess players forum`
  });

  const { data: topics, isLoading } = useQuery({
    queryKey: ["forum-topics", category?.id],
    queryFn: async () => {
      if (!category?.id) return [];
      
      const { data, error } = await supabase
        .from("forum_topics")
        .select(`
          *,
          user_profiles!forum_topics_user_id_fkey (display_name)
        `)
        .eq("category_id", category.id)
        .order("is_pinned", { ascending: false })
        .order("last_posted_at", { ascending: false });
      
      if (error) throw error;
      return data as TopicWithProfile[];
    },
    enabled: !!category?.id,
  });

  if (!category) {
    return (
      <ChessAppLayout>
        <div className="p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Category not found</h1>
            <Button asChild>
              <Link to="/forums">Back to Forums</Link>
            </Button>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <nav className="text-sm text-muted-foreground mb-2">
              <Link to="/forums" className="hover:text-primary">Forums</Link>
              <span className="mx-2">›</span>
              <span>{category.name}</span>
            </nav>
            <h1 className="text-3xl font-bold mb-2">{category.name}</h1>
            <p className="text-muted-foreground">{category.description}</p>
          </div>
          
          {user && (
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Topic
            </Button>
          )}
        </div>

        {/* Topics List */}
        <Card>
          <CardHeader>
            <CardTitle>Topics</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="border-b border-border pb-4">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : topics?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No topics yet. Be the first to start a discussion!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topics?.map((topic) => (
                  <div key={topic.id} className="flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-2">
                      {topic.is_pinned && <Pin className="w-4 h-4 text-primary" />}
                      {topic.is_locked && <Lock className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    
                    <div className="flex-1">
                      <Link 
                        to={`/forums/t/${topic.id}`}
                        className="text-lg font-medium hover:text-primary transition-colors block mb-1"
                      >
                        {topic.title}
                      </Link>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>by {topic.user_profiles?.display_name || "Unknown"}</span>
                        <span>{formatDistanceToNow(new Date(topic.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        <span>{topic.posts_count}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        <span>{topic.views_count}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatDistanceToNow(new Date(topic.last_posted_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ChessAppLayout>
  );
}
